// The network dry runs (`POST /networks/validate`, `POST /networks/{id}/validate`) as New network, the Settings tab
// and the Edit address plan dialog use them: a newer request aborts an older one, and only the last answer counts.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../api/context';
import type {
  CreateNetworkRequest,
  Diagnostic,
  NetworkValidation,
  PatchNetworkRequest,
} from '../../api/types';
import { stableStringify } from '../../lib/json';

export interface ValidationState {
  data: NetworkValidation | null;
  error: unknown;
  pending: boolean;
}

/** `run` sends the dry run of a new network now. */
export function useNewNetworkValidation() {
  const api = useApi();
  const [state, setState] = useState<ValidationState>({ data: null, error: null, pending: false });
  const sequence = useRef(0);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);
  const run = useCallback(
    async (request: CreateNetworkRequest): Promise<NetworkValidation | null> => {
      controller.current?.abort();
      const current = new AbortController();
      controller.current = current;
      const id = ++sequence.current;
      setState((s) => ({ ...s, pending: true }));
      try {
        const data = await api.validateNewNetwork(request, current.signal);
        if (id === sequence.current) setState({ data, error: null, pending: false });
        return data;
      } catch (error) {
        if (current.signal.aborted) return null;
        if (id === sequence.current) setState((s) => ({ ...s, error, pending: false }));
        return null;
      }
    },
    [api],
  );
  return { ...state, run };
}

/**
 * The dry run of a change of network `id`: sent `delayMs` after the patch last changed (400 ms, spec 11 6.15.8 and
 * 6.15.12); null sends nothing. `retry` sends the current patch again at once.
 */
export function useNetworkValidation(id: string, patch: PatchNetworkRequest | null, delayMs = 400) {
  const api = useApi();
  const [state, setState] = useState<ValidationState>({ data: null, error: null, pending: false });
  const key = patch ? stableStringify(patch) : null;
  const latest = useRef(patch);
  useEffect(() => {
    latest.current = patch;
  });
  const sequence = useRef(0);
  const controller = useRef<AbortController | null>(null);
  useEffect(() => () => controller.current?.abort(), []);
  const send = useCallback(async () => {
    const body = latest.current;
    if (!body) return;
    controller.current?.abort();
    const current = new AbortController();
    controller.current = current;
    const n = ++sequence.current;
    setState((s) => ({ ...s, pending: true }));
    try {
      const data = await api.validateNetwork(id, body, current.signal);
      if (n === sequence.current) setState({ data, error: null, pending: false });
    } catch (error) {
      if (current.signal.aborted) return;
      if (n === sequence.current) setState((s) => ({ ...s, error, pending: false }));
    }
  }, [api, id]);
  useEffect(() => {
    if (key === null) {
      controller.current?.abort();
      sequence.current++;
      setState({ data: null, error: null, pending: false });
      return;
    }
    const timer = setTimeout(() => void send(), delayMs);
    return () => clearTimeout(timer);
  }, [key, delayMs, send]);
  return { ...state, retry: () => void send() };
}

/** Diagnostics at a field: the path is the field's pointer or inside it. */
export function diagnosticsAt(diagnostics: readonly Diagnostic[], pointer: string): Diagnostic[] {
  return diagnostics.filter((d) => d.path === pointer || d.path.startsWith(`${pointer}/`));
}

/** The messages of the field's errors and warnings (info diagnostics show in lists, not at fields). */
export function messagesAt(diagnostics: readonly Diagnostic[], pointer: string): string[] {
  return diagnosticsAt(diagnostics, pointer)
    .filter((d) => d.severity !== 'info')
    .map((d) => d.message);
}
